import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import Sidebar from "../shared/Sidebar";
import FileUploadBox from "../shared/UploadFile";

const loadOptions = [
  { value: "Furnace", label: "Furnace" },
  { value: "Extruder", label: "Extruder" },
  { value: "Cooling Towers", label: "Cooling Towers" },
];

const Electrical = () => {
  
 const baseUrl = process.env.baseUrl;
  const navigate = useNavigate();
  const fileInputRef = useRef();

  // --- form state ---
  const [diagramFile, setDiagramFile] = useState(null);
  const [loadDate, setLoadDate] = useState("");
  const [loadTime, setLoadTime] = useState("");
  const [loadFile, setLoadFile] = useState(null);

  const [mvPanels, setMvPanels] = useState([{ size: "", qty: 1 }]);
  const [lvPanels, setLvPanels] = useState([{ size: "", qty: 1 }]);

  const [banks, setBanks] = useState([
    { detuned: "", shunt: "" },
    { detuned: "", shunt: "" },
  ]);

  const [filters, setFilters] = useState([{ type: "", panel: "", installation: "", model: "" }]);
  const [stabilizer, setStabilizer] = useState({ present: "", brand: "", kva: "" });
  const [loads, setLoads] = useState([]);
  const [vfds, setVfds] = useState({ model: "", appType: "", location: "", date: "" });
  const [failures, setFailures] = useState(1);

  // --- validation errors ---
  const [errors, setErrors] = useState({});

  // --- handlers ---
  const handleFileChange = (setter) => e => {
    setter(e.target.files[0]);
  };

  const handleAdd = (arr, setArr, template) => {
    setArr([...arr, { ...template }]);
  };

  const handleChangePanel = (arr, setArr) => (idx, field) => e => {
    const copy = [...arr];
    copy[idx][field] = field === "qty" ? Number(e.target.value) : e.target.value;
    setArr(copy);
  };

  const handleBankChange = (idx, field) => e => {
    const copy = [...banks];
    copy[idx][field] = e.target.value;
    setBanks(copy);
  };

  const handleFilterChange = (idx, field) => e => {
    const copy = [...filters];
    copy[idx][field] = e.target.value;
    setFilters(copy);
  };

  const validate = () => {
    const errs = {};
    if (!diagramFile) errs.diagramFile = "Required";
    if (!loadDate) errs.loadDate = "Required";
    if (!loadTime) errs.loadTime = "Required";
    // require at least one MV panel with size
    if (!mvPanels[0].size) errs.mvPanels = "Define size";
    if (!lvPanels[0].size) errs.lvPanels = "Define size";
    banks.forEach((b,i) => {
      if (!b.detuned) errs[`bank${i}-detuned`] = "Req"; 
      if (!b.shunt)   errs[`bank${i}-shunt`]   = "Req";
    });
    filters.forEach((f,i) => {
      if (!f.type) errs[`filter${i}-type`] = "Req";
    });
    if (!stabilizer.present) errs.stabilizer = "Req";
    if (stabilizer.present === "Yes" && !stabilizer.brand) errs.stabBrand = "Req";
    if (!vfds.model) errs.vfdsModel = "Req";
    if (!loads.length) errs.loads = "Select loads";
    if (failures < 0) errs.failures = "Must be ≥0";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;

    // build form data
    const payload = {
      // diagramFile: diagramFile.name,  // upload separately?
      loadSchedule: { date: loadDate, time: loadTime, file: loadFile?.name },
      mvPanels,
      lvPanels,
      capacitorBanks: banks,
      harmonicFilters: filters,
      stabilizer,
      loads: loads.map(o=>o.value),
      vfds,
      totalFailures: failures,
    };

    try {
      await axios.post("/api/electrical", payload);
      navigate("/hvac");
    } catch (err) {
      console.error(err);
      alert("Submit failed");
    }
  };

  return (
    <div className="container col-lg-9 mt-5">
      <div className="row">
        <div className="col-md-4"><Sidebar /></div>
        <div className="col-md-8">
          <Form onSubmit={handleSubmit} className="form-section">
            {/* Single Line Diagram */}
            <Form.Group className="mb-3">
              <Form.Label>Single Line Diagram</Form.Label>
              <FileUploadBox onFileSelected={file=>setDiagramFile(file)} />
              {errors.diagramFile && <div className="text-danger">{errors.diagramFile}</div>}
            </Form.Group>

            {/* Load Schedule */}
            <Row className="mb-3">
              <Col md={8}>
                <Form.Label>Load Schedule</Form.Label>
                <InputGroup>
                  <Form.Control 
                    type="date" 
                    value={loadDate}
                    onChange={e=>setLoadDate(e.target.value)}
                  />
                  <Form.Control 
                    type="time" 
                    value={loadTime}
                    onChange={e=>setLoadTime(e.target.value)}
                  />
                  <Button variant="link" onClick={()=>fileInputRef.current.click()}>
                    Browse File
                  </Button>
                  <Form.Control
                    type="file"
                    className="d-none"
                    ref={fileInputRef}
                    onChange={handleFileChange(setLoadFile)}
                  />
                </InputGroup>
                {(errors.loadDate||errors.loadTime) && (
                  <div className="text-danger">
                    {errors.loadDate||errors.loadTime}
                  </div>
                )}
              </Col>
            </Row>

            {/* MV & LV Panels */}
            <Row className="mb-3">
              {["MV","LV"].map((t,idx)=>{
                const arr = t==="MV"?mvPanels:lvPanels;
                const setter = t==="MV"?setMvPanels:setLvPanels;
                return (
                  <Col md={8} key={t}>
                    <div className="d-flex justify-content-between">
                      <Form.Label>{t} Panels</Form.Label>
                      <Button variant="link" onClick={()=>handleAdd(arr,setter,{size:"",qty:1})}>
                        + Add Panel
                      </Button>
                    </div>
                    {arr.map((p,i)=>(
                      <InputGroup className="mb-2" key={i}>
                        <Form.Control 
                          placeholder="Size"
                          value={p.size}
                          onChange={handleChangePanel(arr,setter)(i,"size")}
                          isInvalid={!!errors[`${t.toLowerCase()}Panels`]}
                        />
                        <Form.Control 
                          type="number" 
                          min={1} 
                          placeholder="Qty"
                          value={p.qty}
                          onChange={handleChangePanel(arr,setter)(i,"qty")}
                        />
                      </InputGroup>
                    ))}
                    {errors[`${t.toLowerCase()}Panels`] && (
                      <div className="text-danger">{errors[`${t.toLowerCase()}Panels`]}</div>
                    )}
                  </Col>
                );
              })}
            </Row>

            {/* Capacitor Banks */}
            <Form.Group className="mb-3">
              <Row>
                {banks.map((b,i)=>(
                  <Col md={6} key={i}>
                    <Form.Label>Bank {i+1}</Form.Label>
                    <Form.Control 
                      placeholder="Brand of Capacitor"
                      className="mb-2"
                      value={b.brand||""}
                      onChange={e=>handleBankChange(i,"brand")(e)}
                    />
                    <InputGroup className="mb-2">
                      <Form.Control 
                        type="number" 
                        placeholder="KVA Rating"
                        value={b.kva||""}
                        onChange={e=>handleBankChange(i,"kva")(e)}
                      />
                      <InputGroup.Text>KVA</InputGroup.Text>
                    </InputGroup>
                    {["detuned","shunt"].map(f=>(
                      <div key={f}>
                        <Form.Label>{f==="detuned"?"Detuned Filter?":"Shunt Reactor?"}</Form.Label>
                        <div>
                          {["Yes","No"].map(opt=>(
                            <Form.Check
                              key={opt}
                              type="radio"
                              label={opt}
                              name={`${f}${i}`}
                              inline
                              value={opt}
                              checked={banks[i][f]===opt}
                              onChange={handleBankChange(i,f)}
                            />
                          ))}
                        </div>
                        {errors[`bank${i}-${f}`] && (
                          <div className="text-danger">{errors[`bank${i}-${f}`]}</div>
                        )}
                      </div>
                    ))}
                  </Col>
                ))}
              </Row>
            </Form.Group>

            {/* Harmonic Filters */}
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between">
                <Form.Label>Harmonic Filters</Form.Label>
                <Button variant="link" onClick={()=>handleAdd(filters,setFilters,{type:"",panel:"",installation:"",model:""})}>
                  + Add Filter
                </Button>
              </div>
              {filters.map((f,i)=>(
                <Row className="mb-2" key={i}>
                  {["type","panel","installation","model"].map(col=>(
                    <Col key={col}>
                      <Form.Control
                        placeholder={col.charAt(0).toUpperCase()+col.slice(1)}
                        value={f[col]}
                        onChange={handleFilterChange(i,col)}
                        isInvalid={!!errors[`filter${i}-${col}`]}
                      />
                    </Col>
                  ))}
                </Row>
              ))}
            </Form.Group>

            {/* Voltage Stabilizer */}
            <Form.Group className="mb-3">
              <Form.Label>Voltage Stabilizers</Form.Label><br/>
              {["Yes","No"].map(opt=>(
                <Form.Check
                  key={opt}
                  type="radio"
                  label={opt}
                  name="stabilizer"
                  inline
                  value={opt}
                  checked={stabilizer.present===opt}
                  onChange={e=>setStabilizer(s=>({...s,present:e.target.value}))}
                />
              ))}
              {errors.stabilizer && <div className="text-danger">{errors.stabilizer}</div>}
              {stabilizer.present==="Yes" && (
                <>
                  <Form.Control
                    placeholder="Brand"
                    className="mt-2 mb-2"
                    value={stabilizer.brand}
                    onChange={e=>setStabilizer(s=>({...s,brand:e.target.value}))}
                    isInvalid={!!errors.stabBrand}
                  />
                  <InputGroup>
                    <Form.Control
                      type="number"
                      placeholder="KVA Rating"
                      value={stabilizer.kva}
                      onChange={e=>setStabilizer(s=>({...s,kva:e.target.value}))}
                    />
                    <InputGroup.Text>KVA</InputGroup.Text>
                  </InputGroup>
                </>
              )}
            </Form.Group>

            {/* Main Loads */}
            <Form.Group className="mb-3">
              <Form.Label>Main Loads of the Plant</Form.Label>
              <Select
                options={loadOptions}
                isMulti
                value={loads}
                onChange={setLoads}
                placeholder="Select Loads..."
              />
              {errors.loads && <div className="text-danger">{errors.loads}</div>}
            </Form.Group>

            {/* VFDs */}
            <Form.Group className="mb-3">
              <Form.Label>VFDs</Form.Label>
              <Form.Control
                placeholder="Model"
                className="mb-2"
                value={vfds.model}
                onChange={e=>setVfds(v=>({...v,model:e.target.value}))}
                isInvalid={!!errors.vfdsModel}
              />
              <select
                className="form-select mb-2"
                value={vfds.appType}
                onChange={e=>setVfds(v=>({...v,appType:e.target.value}))}
              >
                <option value="">Application Type</option>
                <option value="Public">Public</option>
              </select>
              <Form.Control
                placeholder="Install Location"
                className="mb-2"
                value={vfds.location}
                onChange={e=>setVfds(v=>({...v,location:e.target.value}))}
              />
              <Form.Control
                type="date"
                value={vfds.date}
                onChange={e=>setVfds(v=>({...v,date:e.target.value}))}
              />
            </Form.Group>

            {/* Total Failures */}
            <Form.Group className="mb-3">
              <Form.Label>Total Failures</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={failures}
                onChange={e=>setFailures(Number(e.target.value))}
                isInvalid={!!errors.failures}
              />
              {errors.failures && <div className="text-danger">{errors.failures}</div>}
            </Form.Group>

            <Button type="submit" className="btn btn-custom w-100">
              Continue →
            </Button>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Electrical;
